import type { Media } from '~/models/media';
import type { MediaSet } from '~/models/media-set';
import { RT_URL } from '~/globals/rt-urls';
import { RtElTarget } from '~/models/rt-el-target';

export const extractBodyFromPage = (page: string): string => {
	const start = '<body';
	const end = '</body>';

	return page
		.substring(page.indexOf(start) + start.length, page.indexOf(end))
		.replace(/^[^>]*>/u, '');
};

// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
export const extractMediaFromFrontPageListItem = (item: HTMLLIElement): Media|null => {
	const titleSpan = item.querySelector<HTMLSpanElement>(RtElTarget.TITLE);
	if (!titleSpan) return null;

	const title = titleSpan.textContent as string;

	const urlLink = item.querySelector<HTMLAnchorElement>('a');
	if (!urlLink) return null;

	const href = urlLink.getAttribute('href');
	if (href === null) return null;

	const url = new URL(href, RT_URL);

	const movie: Media = { title, url };

	const scoreSpan = item.querySelector<HTMLSpanElement>(RtElTarget.SCORE);

	if (!scoreSpan) return movie;

	const score = Number(scoreSpan.textContent?.replace('%', ''));

	if (isNaN(score)) return movie;

	return { ...movie, score };
};

// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
export const extractMediaSetFromFrontPageList = (list: HTMLUListElement): MediaSet|null => {
	const heading = list.querySelector<HTMLHeadingElement>(RtElTarget.LIST_TITLE);
	if (!heading) return null;

	const mediaListItems = [...list.querySelectorAll<HTMLLIElement>(RtElTarget.LIST_ITEM)];
	if (mediaListItems.length < 1) return null;

	const { textContent: title } = heading;
	if (title === null || title.length < 1) return null;

	const media = mediaListItems
		// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
		.map(listItem => extractMediaFromFrontPageListItem(listItem))
		.filter(mediaItem => mediaItem !== null) as Media[];

	return { title, media };
};

// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
export const extractMediaFromDynamicListItem = (listItem: HTMLDivElement): Media|null => {
	const titleEl = listItem.querySelector<HTMLSpanElement>(RtElTarget.DYNAMIC_LIST_ITEM_TITLE);
	if (!titleEl) return null;

	const link = listItem.querySelector<HTMLAnchorElement>(RtElTarget.DYNAMIC_LIST_ITEM_LINK);
	if (!link) return null;

	const { textContent: title } = titleEl;
	if (title === null || title === '') return null;

	const href = link.getAttribute('href');
	if (href === null) return null;

	const url = new URL(href, RT_URL);

	const movie: Media = { title, url };

	const scoreEl = listItem.querySelector<HTMLDivElement>(RtElTarget.DYNAMIC_LIST_ITEM_SCORE);
	if (!scoreEl) return movie;

	const scoreString = scoreEl.getAttribute('criticsscore');
	if (scoreString === null || scoreString.length === 0) return movie;
	const score = Number(scoreString);

	if (isNaN(score)) return movie;

	return { ...movie, score };
};

// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
export const extractMediaSetFromDynamicList = (list: HTMLDivElement): MediaSet|null => {
	const heading = list.querySelector<HTMLHeadingElement>(RtElTarget.DYNAMIC_LIST_TITLE);
	if (!heading) return null;

	const mediaListItems = [...list.querySelectorAll<HTMLDivElement>(RtElTarget.DYNAMIC_LIST_ITEM)];
	if (mediaListItems.length < 1) return null;

	const { textContent: title } = heading;
	if (title === null || title.length <= 0) return null;

	const media = mediaListItems
		// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
		.map(listItem => extractMediaFromDynamicListItem(listItem))
		.filter(mediaItem => mediaItem !== null) as Media[];

	return { title, media };
};

// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
export const extractMediaSetsFromFrontPageBody = (body: HTMLBodyElement): MediaSet[] => {
	const frontPageDynamicMovieSets = [...body.querySelectorAll<HTMLDivElement>(RtElTarget.FRONT_PAGE_DYNAMIC_LIST)]
		// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
		.map(list => extractMediaSetFromDynamicList(list));

	const frontPageListMovieSets = [...body.querySelectorAll<HTMLUListElement>(RtElTarget.FRONT_PAGE_MOVIE_LIST)]
		// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
		.map(list => extractMediaSetFromFrontPageList(list));

	return [...frontPageDynamicMovieSets, ...frontPageListMovieSets]
		.filter(movieSet => movieSet !== null) as MediaSet[];
};
